import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaShare, FaCopy, FaYoutube, FaInstagram } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from '../../services/axiosConfig';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

// TruncatedDescription Component
const TruncatedDescription = ({ description, className = "" }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsLargeScreen(window.innerWidth >= 640); // sm breakpoint
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  if (!description) return null;

  const maxChars = isLargeScreen ? 60 : 20;
  const shouldTruncate = description.length > maxChars;
  const displayText = isExpanded || !shouldTruncate ? description : description.substring(0, maxChars);

  return (
    <div className={className}>
      <p className="text-gray-600 text-sm leading-relaxed">
        {displayText}
        {shouldTruncate && !isExpanded && '...'}
        {shouldTruncate && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-orange-600 hover:text-orange-700 text-sm font-medium ml-1 transition-colors"
          >
            {isExpanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </p>
    </div>
  );
};

// Stats Component for Instagram
const InstagramStats = ({ profile, isCard = false }) => {
  const statsContent = (
    <>
      <div className="flex items-center gap-2 mb-3">
        <FaInstagram className="text-pink-500 text-lg" />
        <span className="text-sm font-semibold text-gray-700">Instagram</span>
      </div>
      <div className="flex gap-6 text-sm">
        <div className="text-center">
          <div className="font-bold text-gray-800 text-lg">
            {profile.followersCount?.toLocaleString() || '0'}
          </div>
          <div className="text-gray-600">followers</div>
        </div>
        <div className="text-center">
          <div className="font-bold text-gray-800 text-lg">
            {profile.followsCount?.toLocaleString() || '0'}
          </div>
          <div className="text-gray-600">following</div>
        </div>
        <div className="text-center">
          <div className="font-bold text-gray-800 text-lg">
            {profile.mediaCount?.toLocaleString() || '0'}
          </div>
          <div className="text-gray-600">posts</div>
        </div>
      </div>
    </>
  );

  if (isCard) {
    return (
      <div className="bg-gradient-to-r from-pink-50 to-pink-100 rounded-xl p-4 border border-pink-200">
        {statsContent}
      </div>
    );
  }

  return <div className="mb-6">{statsContent}</div>;
};

// Stats Component for YouTube
const YouTubeStats = ({ profile, isCard = false }) => {
  if (!profile.youtubeConnected) return null;

  const statsContent = (
    <>
      <div className="flex items-center gap-2 mb-3">
        <FaYoutube className="text-red-500 text-lg" />
        <span className="text-sm font-semibold text-gray-700">YouTube</span>
      </div>
      <div className="flex gap-6 text-sm">
        <div className="text-center">
          <div className="font-bold text-gray-800 text-lg">
            {profile.youtubeSubscribers?.toLocaleString() || '0'}
          </div>
          <div className="text-gray-600">subscribers</div>
        </div>
        <div className="text-center">
          <div className="font-bold text-gray-800 text-lg">
            {profile.youtubeVideoCount?.toLocaleString() || '0'}
          </div>
          <div className="text-gray-600">videos</div>
        </div>
        <div className="text-center">
          <div className="font-bold text-gray-800 text-lg">
            {profile.youtubeViewCount?.toLocaleString() || '0'}
          </div>
          <div className="text-gray-600">views</div>
        </div>
      </div>
    </>
  );

  if (isCard) {
    return (
      <div className="bg-gradient-to-r from-red-50 to-red-100 rounded-xl p-4 border border-red-200">
        {statsContent}
      </div>
    );
  }

  return <div className="mb-6">{statsContent}</div>;
};

// Profile Picture Component
const ProfilePicture = ({ profile, size = "md" }) => {
  const sizeClasses = {
    sm: "w-20 h-20 text-xl",
    md: "w-24 h-24 lg:w-28 lg:h-28 text-2xl lg:text-3xl"
  };

  return (
    <div className="flex-shrink-0 relative">
      <div className={`${sizeClasses[size]} rounded-full bg-gradient-to-r from-orange-400 to-pink-400 flex items-center justify-center text-white font-bold`}>
        {profile.profilePictureUrl || profile.profileImage?.url ? (
          <img
            src={profile.profilePictureUrl || profile.profileImage.url}
            alt={profile.username}
            className={`${sizeClasses[size]} rounded-full object-cover`}
          />
        ) : (
          profile.username?.charAt(0).toUpperCase()
        )}
      </div>
    </div>
  );
};

// Action Buttons Component
const ActionButtons = ({ onShare, onCopy, size = "md" }) => {
  const buttonClasses = size === "sm" 
    ? "px-3 py-1.5 text-xs" 
    : "px-4 py-2 text-sm";

  return (
    <div className="flex gap-2">
      <button
        onClick={onShare}
        className={`${buttonClasses} bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors`}
      >
        Share
      </button>
      <button
        onClick={onCopy}
        className={`${buttonClasses} bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors`}
      >
        Copy
      </button>
    </div>
  );
};


const PublicProfile = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProfile = useCallback( async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/auth/links/public/${username}`);

      if (response.data.success) {
        setProfile(response.data.data.user);
        setLinks(response.data.data.links);
      } else {
        setError('Profile not found');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  },[username]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);
  

  const handleLinkClick = async (linkId, url) => {
    try {
      // Track the click
      await axios.post(`/auth/links/track/${linkId}`);

      // Open the link in a new tab
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error tracking click:', error);
      // Still open the link even if tracking fails
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleShare = async () => {
    const url = window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `${profile?.username}'s Links`,
          text: `Check out ${profile?.username}'s links on Infuber`,
          url: url
        });
      } catch (error) {
        console.log('Error sharing:', error);
        copyToClipboard(url);
      }
    } else {
      copyToClipboard(url);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Link copied to clipboard!');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error('Failed to copy link');
    }
  };

  const handleShareLink = async (link) => {
    const shareData = {
      title: link.title,
      text: link.description || `Check out this link from ${profile?.username}`,
      url: link.url
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        console.log('Error sharing:', error);
        copyToClipboard(link.url);
      }
    } else {
      copyToClipboard(link.url);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gradient-to-br from-white-50 to-pink-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading profile...</p>
          </div>
        </div>
      </>
    );
  }

  if (error || !profile) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gradient-to-br from-white-50 to-pink-50 flex items-center justify-center">
          <div className="text-center">
            <div className="text-6xl mb-4">😔</div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Profile Not Found</h1>
            <p className="text-gray-600 mb-6">The profile you're looking for doesn't exist or is private.</p>
            <button
              onClick={() => navigate('/influencer')}
              className="bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 transition-colors"
            >
              <FaArrowLeft className="inline mr-2" />
              Back to Influencers
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen flex flex-col">
      <Navbar />

      {/* Instagram-style Profile Layout */}
      <div className="flex-grow max-w-4xl mx-auto w-full px-2 py-3">
        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/influencer')}
            className="flex items-center text-gray-600 hover:text-gray-800 transition-colors"
          >
            <FaArrowLeft className="mr-2" />
            Back
          </button>
        </div>

        {/* Profile Header - Responsive Design */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6">
          {/* Small Screen Layout (mobile) */}
          <div className="block md:hidden">
            {/* Profile Picture and Username Row */}
            <div className="flex items-center gap-4 mb-6">
              <ProfilePicture profile={profile} size="sm" />
              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-bold text-gray-800 truncate">
                  @{profile.username}
                </h1>
                <div className="mt-2">
                  <ActionButtons 
                    onShare={handleShare} 
                    onCopy={() => copyToClipboard(window.location.href)} 
                    size="sm" 
                  />
                </div>
              </div>
            </div>

            {/* Instagram Stats */}
            <InstagramStats profile={profile} />

            {/* YouTube Stats */}
            <YouTubeStats profile={profile} />

            {/* Bio Section */}
            <div className="mb-4">
              <div className="text-gray-700 text-sm leading-relaxed">
                {profile.bio || profile.description || (
                  <span className="text-gray-500 italic">
                    {profile.contentType && profile.contentType.length > 0
                      ? profile.contentType.map(category =>
                        category.charAt(0).toUpperCase() + category.slice(1)
                      ).join(' • ')
                      : 'Content Creator'
                    }
                  </span>
                )}
              </div>
              {profile.accountType && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-800">
                    {profile.accountType.charAt(0).toUpperCase() + profile.accountType.slice(1)} Account
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Medium and Large Screen Layout */}
          <div className="hidden md:block">
            <div className="flex items-start gap-6">
              {/* Profile Picture */}
              <ProfilePicture profile={profile} size="md" />

              {/* Profile Info and Stats */}
              <div className="flex-1 min-w-0">
                {/* Username and Action Buttons */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                    <h1 className="text-2xl lg:text-3xl font-bold text-gray-800">
                      @{profile.username}
                    </h1>
                    <ActionButtons 
                      onShare={handleShare} 
                      onCopy={() => copyToClipboard(window.location.href)} 
                      size="md" 
                    />
                  </div>
                </div>

                {/* Stats Row - Instagram and YouTube side by side */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  <InstagramStats profile={profile} isCard={true} />
                  <YouTubeStats profile={profile} isCard={true} />
                </div>

                {/* Bio Section */}
                <div className="mb-4">
                  <div className="text-gray-700 text-sm leading-relaxed">
                    {profile.bio || profile.description || (
                      <span className="text-gray-500 italic">
                        {profile.contentType && profile.contentType.length > 0
                          ? profile.contentType.map(category =>
                            category.charAt(0).toUpperCase() + category.slice(1)
                          ).join(' • ')
                          : 'Content Creator'
                        }
                      </span>
                    )}
                  </div>
                  {profile.accountType && (
                    <div className="mt-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-800">
                        {profile.accountType.charAt(0).toUpperCase() + profile.accountType.slice(1)} Account
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Links Grid - Enhanced Style */}
        <div className="bg-white rounded-2xl shadow-lg p-4 border border-gray-100">
          <h3 className="text-2xl font-bold text-gray-800 mb-6">Links{links.length > 0 ? `(${links.length})` : ''}</h3>
          {links.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-pink-400 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white text-2xl">🔗</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3">No public links yet</h3>
              <p className="text-gray-600 max-w-md mx-auto">This user hasn't shared any public links yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {links.map((link, index) => (
                <div
                  key={link._id}
                  className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-4 group border border-gray-200 hover:border-orange-200"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <h4 className="text-xl font-bold text-gray-800 group-hover:text-orange-600 transition-colors cursor-pointer"
                            onClick={() => handleLinkClick(link._id, link.url)}>
                            {link.title}
                          </h4>
                          <span className="px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700 border border-green-200">
                            Public
                          </span>
                        </div>

                        <div className="flex items-center gap-2 mb-3">
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:text-blue-700 transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLinkClick(link._id, link.url);
                            }}
                          >
                            {link.url}
                          </a>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(link.url);
                            }}
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-all duration-200"
                            title="Copy Link"
                          >
                            <FaCopy className="text-sm" />
                          </button>
                        </div>

                        <TruncatedDescription
                          description={link.description}
                          className="mb-0"
                        />
                      </div>
                    </div>

                    <div className="flex items-center space-x-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShareLink(link);
                        }}
                        className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all duration-200"
                        title="Share Link"
                      >
                        <FaShare />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
      <Footer />
    </div>
  );
};

export default PublicProfile;
